
import React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Region, RegionFormData } from '@/types/regions';

const regionFormSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  code: z.string().min(2, 'Code must be at least 2 characters').max(5, 'Code must be at most 5 characters'),
  description: z.string().optional(),
  active: z.boolean(),
});

interface RegionFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  editingRegion: Region | null;
  onSubmit: (data: RegionFormData) => Promise<void>;
}

const RegionFormDialog = ({
  open,
  onOpenChange,
  editingRegion,
  onSubmit,
}: RegionFormDialogProps) => {
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<RegionFormData>({
    resolver: zodResolver(regionFormSchema),
    defaultValues: {
      name: '',
      code: '',
      description: '',
      active: true,
    },
  });

  React.useEffect(() => {
    if (editingRegion) {
      reset({
        name: editingRegion.name,
        code: editingRegion.code,
        description: editingRegion.description || '',
        active: editingRegion.active,
      });
    } else {
      reset({
        name: '',
        code: '',
        description: '',
        active: true,
      });
    }
  }, [editingRegion, reset]);

  const handleFormSubmit = async (data: RegionFormData) => {
    await onSubmit(data);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>
            {editingRegion ? 'Edit Region' : 'Add New Region'}
          </DialogTitle>
          <DialogDescription>
            {editingRegion
              ? 'Update the region information.'
              : 'Create a new region to organize countries.'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">Name</Label>
            <Input
              id="name"
              placeholder="e.g., Caribbean"
              {...register('name')}
            />
            {errors.name && (
              <p className="text-sm text-red-500">{errors.name.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="code">Code</Label>
            <Input
              id="code"
              placeholder="e.g., CAR"
              {...register('code')}
              style={{ textTransform: 'uppercase' }}
            />
            {errors.code && (
              <p className="text-sm text-red-500">{errors.code.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              placeholder="Optional description of the region"
              {...register('description')}
              rows={3}
            />
            {errors.description && (
              <p className="text-sm text-red-500">{errors.description.message}</p>
            )}
          </div>

          <div className="flex items-center space-x-2">
            <Switch
              id="active"
              checked={watch('active')}
              onCheckedChange={(checked) => setValue('active', checked)}
            />
            <Label htmlFor="active">Active</Label>
          </div>

          <div className="flex justify-end space-x-2 pt-4">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {editingRegion ? 'Update' : 'Create'} Region
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default RegionFormDialog;
